/**
 * FeaturesPage
 * /features
 *
 * Real content per marketing-content-brief.json. Eight feature modules,
 * each with a specific icon, one-sentence description, 3-4 capability bullets,
 * business outcome sentence, and a relevant product screenshot.
 *
 * Uses Phosphor icons and Framer Motion via AnimatedSection / StaggerItem,
 * which already respect useReducedMotion().
 */

import { Button } from '@/components/ui/button';
import { SEOHead } from '@/components/seo/SEOHead';
import { SchemaInjector } from '@/components/seo/SchemaInjector';
import { softwareApplicationSchema } from '@/lib/schemas';
import { Link } from 'react-router-dom';
import {
  AnimatedSection,
  StaggerContainer,
  StaggerItem,
  SectionDivider,
} from '@/components/marketing';
import { getAuthLink } from '@/lib/links';
import { useSanityPage } from '@/lib/sanity.hooks';
import {
  AddressBook,
  Calculator,
  Presentation,
  Megaphone,
  CloudRain,
  SquaresFour,
  CreditCard,
  PlugsConnected,
  ArrowRight,
  CheckCircle,
} from '@phosphor-icons/react';

/* ─── SEO schemas ─────────────────────────────────────────────────── */
const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://rooflyzer.pro' },
    { '@type': 'ListItem', position: 2, name: 'Features', item: 'https://rooflyzer.pro/features' },
  ],
};

/* ─── Hero stat row data ──────────────────────────────────────────── */
const heroStats = [
  { value: '80+', label: 'Integrated Features' },
  { value: '8', label: 'Core Modules' },
  { value: '2.5 hr', label: 'Saved Per Estimate' },
  { value: '40%', label: 'Higher Close Rates' },
];

/* ─── Feature module data ─────────────────────────────────────────── */
const featureModules = [
  {
    id: 'crm',
    title: 'CRM & Lead Management',
    description:
      'Centralized contact database with lifecycle tracking from lead to customer to re-nurture.',
    icon: AddressBook,
    screenshot: '/product-frames/pipeline.webp',
    screenshotAlt: 'Rooflyzer CRM pipeline and contact management view',
    learnMoreUrl: '/features/crm',
    bullets: [
      {
        title: 'Unified contact hub',
        desc: 'Lead, customer, and spam management in one database.',
      },
      { title: 'Pipeline view', desc: 'Kanban board and list toggle for every stage of the sale.' },
      {
        title: 'Lead scoring',
        desc: 'Qualification rules that rank the hottest opportunities first.',
      },
      {
        title: 'Re-nurture queue',
        desc: 'Stalled leads automatically queue for targeted follow-up.',
      },
    ],
    businessValue:
      'Reduce lead loss from 30% to under 5%, track every lead source with attribution, and automate follow-ups.',
  },
  {
    id: 'estimates',
    title: 'Instant Estimates',
    description:
      'Embeddable widget for contractor websites that captures leads and generates ballpark roof estimates using ArcGIS building footprints or Google Solar API.',
    icon: Calculator,
    screenshot: '/product-frames/instant-quote-footprint.webp',
    screenshotAlt: 'Instant estimate widget detecting a roof footprint from satellite imagery',
    learnMoreUrl: '/features/estimating',
    bullets: [
      { title: 'Address lookup', desc: 'Auto-complete pulls the property in seconds.' },
      {
        title: 'ArcGIS footprint data',
        desc: 'Free building footprints power quick measurements.',
      },
      { title: 'Google Solar API', desc: 'Optional paid integration for higher accuracy.' },
      { title: 'White-label branding', desc: 'Match your logo, colors, and website styling.' },
    ],
    businessValue:
      'Capture 40% more website visitors as leads, give homeowners instant pricing, and pre-qualify leads before sales calls.',
  },
  {
    id: 'proposals',
    title: 'Proposal Presenter',
    description:
      'Interactive proposal creation with tablet-optimized presenter mode for in-home sales, including tiered pricing, financing calculator, and digital signature capture.',
    icon: Presentation,
    screenshot: '/product-frames/estimates.webp',
    screenshotAlt: 'Rooflyzer proposal builder with tiered pricing options',
    learnMoreUrl: '/features/proposals',
    bullets: [
      { title: 'Proposal templates', desc: 'Variable substitution builds proposals in minutes.' },
      { title: 'Good / Better / Best tiers', desc: 'Show options that raise average deal size.' },
      { title: 'Presenter mode', desc: 'Tablet-optimized flow for in-home sales.' },
      { title: 'Digital signatures', desc: 'Capture e-signatures on the spot or via public link.' },
    ],
    businessValue:
      'Close 20% more deals in-home, cut proposal creation from 30 minutes to 5 minutes, and eliminate paper contracts.',
  },
  {
    id: 'marketing',
    title: 'Marketing Automation',
    description:
      'Email and SMS campaign builder with journey sequences, form tracking, and attribution analytics.',
    icon: Megaphone,
    screenshot: '/product-frames/marketing.webp',
    screenshotAlt: 'Rooflyzer marketing automation campaign builder',
    learnMoreUrl: '/features/marketing-automation',
    bullets: [
      {
        title: 'Email & SMS builder',
        desc: 'Campaigns and templates for every follow-up scenario.',
      },
      { title: 'Journey sequences', desc: 'Multi-step automation that nurtures while you work.' },
      { title: 'Form & embed tracking', desc: 'Know which forms and widgets generate each lead.' },
      { title: 'Attribution analytics', desc: 'ROI by source, campaign, and keyword.' },
    ],
    businessValue:
      'Automate 80% of follow-up tasks, increase lead response rates by 3x, and track ROI by campaign and source.',
  },
  {
    id: 'storm',
    title: 'Storm Intelligence',
    description:
      'Weather-based lead generation with storm tracking, territory management, and prospect targeting for hail and wind damage areas.',
    icon: CloudRain,
    screenshot: '/product-frames/canvassing.webp',
    screenshotAlt: 'Rooflyzer storm intelligence and territory canvassing map',
    learnMoreUrl: '/features/storm-canvassing',
    bullets: [
      { title: 'Storm monitoring dashboard', desc: 'Track hail and wind events in real time.' },
      {
        title: 'Zip code targeting',
        desc: 'Territory management by the areas you actually serve.',
      },
      { title: 'Prospect lists', desc: 'Generate address lists from the storm path.' },
      { title: 'Canvassing hub', desc: 'Route planning and work window optimization.' },
    ],
    businessValue:
      'Target storm-damaged areas within 24 hours, increase canvassing efficiency by 50%, and stop wasting trips on undamaged properties.',
  },
  {
    id: 'project-management',
    title: 'Project Management',
    description:
      'Job tracking from proposal to completion with scheduling, materials ordering, and crew management.',
    icon: SquaresFour,
    screenshot: '/product-frames/dashboard.webp',
    screenshotAlt: "Rooflyzer command center with job pipeline, crew load, and today's focus",
    learnMoreUrl: '/features/project-management',
    bullets: [
      { title: 'Job pipeline', desc: 'Stage tracking from signed to complete.' },
      { title: 'Calendar integration', desc: 'Scheduling that connects jobs and crews.' },
      { title: 'Materials order forms', desc: 'Order what you need without leaving the job.' },
      { title: 'Crew assignment', desc: 'Assign teams and track profitability in real time.' },
    ],
    businessValue:
      'Reduce job delays by 40%, improve crew utilization, and track job profitability in real time.',
  },
  {
    id: 'invoicing',
    title: 'Invoicing & Payments',
    description:
      'Invoice generation with QuickBooks sync, Stripe payment processing, and automated payment reminders.',
    icon: CreditCard,
    screenshot: '/product-frames/reports.webp',
    screenshotAlt: 'Rooflyzer invoicing and financial dashboard',
    learnMoreUrl: '/features/invoicing',
    bullets: [
      { title: 'Invoice templates', desc: 'Branded line-item invoices in one click.' },
      { title: 'QuickBooks sync', desc: 'OAuth connection is in progress for automatic posting.' },
      { title: 'Stripe payments', desc: 'Collect cards and bank transfers online.' },
      { title: 'Automated reminders', desc: 'Follow up on overdue balances automatically.' },
    ],
    businessValue:
      'Reduce accounts receivable days by 50%, eliminate manual QuickBooks entry, and get paid faster with online payments.',
  },
  {
    id: 'integrations',
    title: 'Integrations',
    description:
      'Pre-built integrations with QuickBooks, Stripe, Google Ads, call tracking, and a WordPress plugin.',
    icon: PlugsConnected,
    screenshot: '/product-frames/dashboard.webp',
    screenshotAlt: 'Rooflyzer dashboard showing integrated modules',
    learnMoreUrl: '/integrations',
    bullets: [
      { title: 'QuickBooks OAuth', desc: 'Sync financial data without double entry.' },
      {
        title: 'Stripe payment processing',
        desc: 'Payments flow straight into the invoice record.',
      },
      { title: 'Google Ads & call tracking', desc: 'Connect ad spend and calls to closed deals.' },
      { title: 'WordPress plugin', desc: 'Embed the estimate widget with one click.' },
    ],
    businessValue:
      'Eliminate manual data entry between systems, sync financial data automatically, and extend the platform with the tools you already use.',
  },
];

/* ─────────────────────────────────────────────────────────────────── */

export const FeaturesPage = () => {
  const { data: cmsData } = useSanityPage('features');

  const pageTitle = cmsData?.seo?.metaTitle || 'Roofing Software Features — CRM, Estimating & More';
  const pageDescription =
    cmsData?.seo?.metaDescription ||
    cmsData?.description ||
    'From lead capture to final payment, Rooflyzer OS handles it all.';
  const heroTitle = cmsData?.title || 'Everything You Need to Run Your Roofing Business';
  const heroSubtitle =
    cmsData?.description || 'From lead capture to final payment, Rooflyzer OS handles it all.';

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDescription}
        canonicalUrl="/features"
        keywords="roofing software features, roofing CRM features, roofing estimating software, roofing project management"
        ogImage={cmsData?.seo?.ogImage?.asset?.url || '/social-card.svg'}
        schema={breadcrumbSchema}
      />
      <SchemaInjector
        schema={softwareApplicationSchema({
          description: pageDescription,
          url: 'https://rooflyzer.pro/features',
          offers: { price: '0', description: 'Free tier available' },
        })}
      />

      <div className="pt-24 min-h-screen pb-20">
        {/* ── Hero ──────────────────────────────────────────────────── */}
        <section className="relative text-center max-w-5xl mx-auto mb-16 px-4 sm:px-6 overflow-hidden">
          <AnimatedSection className="relative z-10">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-slate-800/80 border border-slate-700 text-slate-300 text-xs font-mono font-medium uppercase tracking-wider mb-6">
              <SquaresFour className="size-4" />
              <span>Full Feature Breakdown</span>
            </div>

            <h1 className="hero-h1 font-bold text-white mb-5">
              {cmsData?.title ? (
                heroTitle
              ) : (
                <>
                  Everything You Need to{' '}
                  <span className="headline-gradient">Run Your Roofing Business</span>
                </>
              )}
            </h1>

            <p className="hero-lead text-white/75 mb-8 max-w-2xl mx-auto">{heroSubtitle}</p>

            <div className="flex flex-wrap items-center justify-center gap-4 mb-10">
              <a href={getAuthLink('/register')}>
                <Button
                  size="lg"
                  className="bg-white text-slate-950 hover:bg-slate-100 font-semibold px-8 rounded-full shadow-sm"
                >
                  Start Free Trial
                  <ArrowRight className="ml-2 size-4" />
                </Button>
              </a>
              <Link to="/demo">
                <Button
                  size="lg"
                  variant="ghost"
                  className="border border-white/20 text-white hover:bg-white/10 px-8 rounded-full"
                >
                  Watch Demo
                </Button>
              </Link>
            </div>

            {/* Static stat row */}
            <StaggerContainer className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-3xl mx-auto">
              {heroStats.map((stat) => (
                <StaggerItem
                  key={stat.label}
                  className="bg-white/[0.03] border border-white/10 rounded-2xl p-4 text-center"
                >
                  <p className="text-3xl sm:text-4xl font-bold text-white tracking-tight">
                    {stat.value}
                  </p>
                  <p className="text-white/50 text-xs mt-1">{stat.label}</p>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </AnimatedSection>
        </section>

        <SectionDivider className="mb-16 max-w-5xl mx-auto px-4" />

        {/* ── Framed Product Screenshot ──────────────────────────── */}
        <AnimatedSection className="mb-20 sm:mb-28 max-w-6xl mx-auto px-4 sm:px-6">
          <div className="relative rounded-3xl overflow-hidden border border-white/10 shadow-2xl glass-panel-elevated">
            <div className="absolute inset-0 rounded-3xl border border-white/[0.06] z-20 pointer-events-none" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent z-10" />
            <img
              src="/product-frames/dashboard.webp"
              alt="Rooflyzer OS dashboard — complete roofing business management software"
              className="w-full h-auto"
              loading="lazy"
            />
            <div className="absolute bottom-6 left-6 z-20 flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-emerald-400" />
              <span className="px-3 py-1 glass-panel-sm rounded-full text-sm text-white/90">
                Live product — updated weekly
              </span>
            </div>
          </div>
        </AnimatedSection>

        {/* ── Feature Module Sections ──────────────────────────────── */}
        {featureModules.map((module, index) => (
          <FeatureModuleSection key={module.id} {...module} reverse={index % 2 === 1} />
        ))}

        <SectionDivider className="mb-0 max-w-5xl mx-auto px-4" />

        {/* ── Final CTA ─────────────────────────────────────────────── */}
        <section className="relative py-24 px-4 text-center overflow-hidden">
          <AnimatedSection className="relative z-10 max-w-3xl mx-auto">
            <div className="bg-slate-900/80 border border-slate-800 rounded-3xl p-10 sm:p-14 shadow-2xl">
              <div className="w-12 h-12 rounded-xl bg-slate-800 border border-slate-700 flex items-center justify-center mx-auto mb-4">
                <PlugsConnected className="size-6 text-slate-200" />
              </div>
              <h2 className="section-h2 font-bold text-white mb-4">
                Ready to replace your messy tech stack?
              </h2>
              <p className="text-white/60 body-lg mb-8 max-w-xl mx-auto">
                One platform. Eight integrated modules. Free during beta. No credit card needed.
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <Button
                  size="lg"
                  className="bg-white text-slate-950 hover:bg-slate-100 font-semibold px-10 rounded-full shadow-sm"
                  onClick={() => window.location.assign(getAuthLink('/register'))}
                >
                  Get Started Free
                  <ArrowRight className="ml-2 size-4" />
                </Button>
                <Link to="/pricing">
                  <Button
                    size="lg"
                    variant="ghost"
                    className="border border-white/20 text-white hover:bg-white/10 px-8 rounded-full"
                  >
                    View Pricing
                  </Button>
                </Link>
              </div>
            </div>
          </AnimatedSection>
        </section>
      </div>
    </>
  );
};

/* ─── FeatureModuleSection sub-component ─────────────────────────── */

interface FeatureModuleSectionProps {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  bullets: Array<{ title: string; desc: string }>;
  businessValue: string;
  screenshot: string;
  screenshotAlt: string;
  learnMoreUrl?: string;
  reverse?: boolean;
}

const FeatureModuleSection = ({
  id,
  title,
  description,
  icon: Icon,
  bullets,
  businessValue,
  screenshot,
  screenshotAlt,
  learnMoreUrl,
  reverse = false,
}: FeatureModuleSectionProps) => {
  return (
    <section id={id} className="relative py-20 sm:py-28 px-4 sm:px-6 overflow-hidden">
      {reverse && <div className="absolute inset-0 bg-white/[0.015] pointer-events-none" />}

      <div className="relative max-w-6xl mx-auto">
        <AnimatedSection>
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            {/* Screenshot column */}
            <div className={reverse ? 'lg:order-2' : ''}>
              <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent z-10" />
                <img
                  src={screenshot}
                  alt={screenshotAlt}
                  className="w-full h-auto"
                  loading="lazy"
                />
                <div className="absolute bottom-4 left-4 z-20">
                  <span className="px-3 py-1 glass-panel-sm rounded-full text-sm text-white/90 border border-white/20">
                    Live product screenshot
                  </span>
                </div>
              </div>
            </div>

            {/* Content column */}
            <div className={reverse ? 'lg:order-1' : ''}>
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-slate-700 bg-slate-800/80 text-slate-300 text-xs font-mono font-medium uppercase tracking-wider mb-5">
                <Icon className="w-4 h-4" />
                <span>{title}</span>
              </div>

              <h2 className="section-h2 font-bold text-white mb-4">{title}</h2>
              <p className="body-lg text-white/65 mb-6">{description}</p>

              <StaggerContainer className="space-y-4 mb-6">
                {bullets.map((bullet, i) => (
                  <StaggerItem key={i} className="flex gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-semibold text-white text-sm">{bullet.title}</h3>
                      <p className="text-white/60 text-sm">{bullet.desc}</p>
                    </div>
                  </StaggerItem>
                ))}
              </StaggerContainer>

              <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4 mb-6">
                <p className="text-white/75 text-sm leading-relaxed">
                  <span className="font-semibold text-white">Business Impact: </span>
                  {businessValue}
                </p>
              </div>

              {learnMoreUrl && (
                <Link
                  to={learnMoreUrl}
                  className="inline-flex items-center text-sm font-medium text-slate-300 hover:text-white transition-colors"
                >
                  Learn more about {title}
                  <ArrowRight className="ml-1 size-4" />
                </Link>
              )}
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};
